import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode } from '@reconcept-platform/shared/data-access';
import {
  type CreateWorkspaceRoleInput,
  type CreateWorkspaceRoleMutationMutation,
  type CreateWorkspaceRoleMutationMutationVariables,
  type DestroyWorkspaceRoleInput,
  type DestroyWorkspaceRoleMutationMutation,
  type DestroyWorkspaceRoleMutationMutationVariables,
  type UpdateWorkspaceRoleInput,
  type UpdateWorkspaceRoleMutationMutation,
  type UpdateWorkspaceRoleMutationMutationVariables,
  type WorkspaceRoleFragmentFragment,
  type WorkspaceRoleQueryQuery,
  type WorkspaceRoleQueryQueryVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { invalidateAllWorkspaces } from '../../utils/cache-invalidators.utils';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { dashboardWithRolesQuery } from '../dashboard/dashboard.queries';
import { workspaceWithRolesQuery } from '../workspace/workspace.queries';
import { WorkspaceRoleQuery } from './workspace-role.queries';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceRoleApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  watchWorkspaceRole(roleId: string): Observable<WorkspaceRoleFragmentFragment> {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceRoleQueryQuery, WorkspaceRoleQueryQueryVariables>({
        query: WorkspaceRoleQuery,
        variables: {
          id: roleId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'WorkspaceRole')));
  }

  createWorkspaceRole(createWorkspaceRoleInput: CreateWorkspaceRoleInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<CreateWorkspaceRoleMutationMutation, CreateWorkspaceRoleMutationMutationVariables>({
          mutation: gql`
            mutation CreateWorkspaceRoleMutation($input: CreateWorkspaceRoleInput!) {
              createWorkspaceRole(input: $input) {
                workspaceRole {
                  id
                  title
                  accessLevel
                  user {
                    id
                  }
                  workspace {
                    id
                  }
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: createWorkspaceRoleInput,
          },
          refetchQueries: [
            {
              query: workspaceWithRolesQuery,
              variables: {
                id: createWorkspaceRoleInput.workspaceId,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createWorkspaceRole.workspaceRole))
    );
  }

  updateWorkspaceRole(updateWorkspaceRoleInput: UpdateWorkspaceRoleInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateWorkspaceRoleMutationMutation, UpdateWorkspaceRoleMutationMutationVariables>({
          mutation: gql`
            mutation UpdateWorkspaceRoleMutation($input: UpdateWorkspaceRoleInput!) {
              updateWorkspaceRole(input: $input) {
                workspaceRole {
                  id
                  title
                  accessLevel
                  user {
                    id
                  }
                  workspace {
                    id
                  }
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: updateWorkspaceRoleInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateWorkspaceRole.workspaceRole))
    );
  }

  destroyWorkspaceRole(
    destroyWorkspaceRoleInput: DestroyWorkspaceRoleInput,
    workspaceId: string,
    parentDashboardId?: string,
  ) {
    const cascade = destroyWorkspaceRoleInput.alsoParentDashboardAndWorkspaceRolesWhereViewerCanManage;

    const refetchQueries = [
      {
        query: workspaceWithRolesQuery,
        variables: {
          id: workspaceId,
        },
      },
    ];

    // the cascade also destroys the user's roles on the parent dashboard, so refetch its role list
    if (cascade && parentDashboardId) {
      refetchQueries.push({
        query: dashboardWithRolesQuery,
        variables: {
          id: parentDashboardId,
        },
      });
    }

    return (
      this.workspaceGraphQLService
        .mutateV2<DestroyWorkspaceRoleMutationMutation, DestroyWorkspaceRoleMutationMutationVariables>({
          mutation: gql`
            mutation DestroyWorkspaceRoleMutation($input: DestroyWorkspaceRoleInput!) {
              destroyWorkspaceRole(input: $input) {
                workspaceRole {
                  id
                }
                errors {
                  path
                  message
                }
              }
            }
          `,
          variables: {
            input: destroyWorkspaceRoleInput,
          },
          refetchQueries,
          // the cascade also destroys roles in sibling workspaces unknown to the client,
          // so evict every cached workspace role list
          update: cascade ? invalidateAllWorkspaces(['workspaceRoles']) : undefined,
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.destroyWorkspaceRole.workspaceRole))
    );
  }
}
